import { useState } from "react"
import { trpc } from "~/lib/trpc"
import { Button, Input } from "@aix/ui"
import { useNavigate } from "@tanstack/react-router"
import { Building2, Plus, Loader2, X } from "lucide-react"
import toast from "react-hot-toast"

export function CompaniesScreen() {
  const [statusFilter, setStatusFilter] = useState<"active" | "archived" | "all">("active")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    infisicalPath: "",
    description: "",
    color: "#3B82F6",
  })
  const navigate = useNavigate()

  const { data, isLoading, refetch } = trpc.companies.list.useQuery({
    status: statusFilter,
  })

  const seedMutation = trpc.companies.seedInitialCompanies.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  const createMutation = trpc.companies.create.useMutation({
    onSuccess: () => {
      toast.success("Company created successfully")
      setShowCreateModal(false)
      setFormData({ name: "", slug: "", infisicalPath: "", description: "", color: "#3B82F6" })
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create company")
    },
  })

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.slug || !formData.infisicalPath) {
      toast.error("Please fill in all required fields")
      return
    }
    createMutation.mutate({
      name: formData.name,
      slug: formData.slug.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
      infisicalPath: formData.infisicalPath,
      description: formData.description || undefined,
      color: formData.color || undefined,
    })
  }

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
      infisicalPath: `/${name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="w-7 h-7 sm:w-8 sm:h-8" />
              Companies
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Manage your businesses and brands</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {(!data?.companies || data.companies.length === 0) && (
              <Button
                onClick={() => seedMutation.mutate()}
                loading={seedMutation.isLoading}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Seed Companies
              </Button>
            )}
            <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {["active", "archived", "all"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === status
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Empty State */}
        {(!data?.companies || data.companies.length === 0) && (
          <div className="text-center py-12 sm:py-16">
            <Building2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">No companies yet</h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-6">
              Get started by seeding initial companies or creating a new one
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => seedMutation.mutate()}
                loading={seedMutation.isLoading}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Seed Initial Companies
              </Button>
              <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Create Manually
              </Button>
            </div>
          </div>
        )}

        {/* Company Cards Grid */}
        {data?.companies && data.companies.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {data.companies.map((company) => (
              <div
                key={company.id}
                className="group p-4 sm:p-6 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 hover:shadow-md transition-all duration-200"
                onClick={() =>
                  navigate({
                    to: "/companies/$slug",
                    params: { slug: company.slug },
                  })
                }
              >
                <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                  {company.color ? (
                    <div
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex-shrink-0 shadow-sm"
                      style={{ backgroundColor: company.color }}
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg text-foreground truncate group-hover:text-primary transition-colors">
                      {company.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{company.slug}</p>
                  </div>
                </div>

                {company.description && (
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-3">{company.description}</p>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                  <span className="truncate">
                    Infisical: <span className="font-mono">{company.infisicalPath}</span>
                  </span>
                  {company.status === "archived" && (
                    <span className="px-2 py-1 rounded bg-destructive/10 text-destructive text-xs font-medium flex-shrink-0 ml-2">
                      Archived
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Create New Company</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-muted rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company Name <span className="text-destructive">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Acme Corp"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Slug <span className="text-destructive">*</span>
                </label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g., acme-corp"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">URL-friendly identifier</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Infisical Path <span className="text-destructive">*</span>
                </label>
                <Input
                  value={formData.infisicalPath}
                  onChange={(e) => setFormData({ ...formData, infisicalPath: e.target.value })}
                  placeholder="e.g., /acme-corp"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">Path for secrets management</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the company"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Brand Color</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isLoading} className="flex-1">
                  Create Company
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
